import { Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  hasFullAccess,
  hasNoAccess,
  hasSpecificAccess,
  SubscriptionSettings,
  SubscriptionWithRelations,
} from "../../types/subscription";
import AdminModalShell from "./AdminModalShell";
import SubscriptionAccessEditor, { SubscriptionAccessType } from "./SubscriptionAccessEditor";

interface ProviderItem {
  id: number;
  title: string;
  visibility: string;
}

interface EditSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (subscriptionId: number, settings: SubscriptionSettings) => Promise<void>;
  subscription: SubscriptionWithRelations;
  providerPortfolios: ProviderItem[];
  providerBaskets: ProviderItem[];
  contentLoading: boolean;
}

function getAccessType(ids: number[]): SubscriptionAccessType {
  if (hasFullAccess(ids)) return "all";
  if (hasNoAccess(ids)) return "none";
  return "specific";
}

const EditSubscriptionModal: React.FC<EditSubscriptionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  subscription,
  providerPortfolios,
  providerBaskets,
  contentLoading,
}) => {
  // Access settings state
  const [portfolioAccess, setPortfolioAccess] = useState<SubscriptionAccessType>("all");
  const [basketAccess, setBasketAccess] = useState<SubscriptionAccessType>("all");
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState<number[]>([]);
  const [selectedBasketIds, setSelectedBasketIds] = useState<number[]>([]);
  const [isExternal, setIsExternal] = useState(false);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Initialize state from subscription when modal opens
  useEffect(() => {
    if (isOpen && subscription) {
      const portfolioIds = subscription.subscriptionSettings.portfolios;
      const basketIds = subscription.subscriptionSettings.baskets;

      setPortfolioAccess(getAccessType(portfolioIds));
      setBasketAccess(getAccessType(basketIds));

      // Set selected IDs only if specific access
      if (hasSpecificAccess(portfolioIds)) {
        setSelectedPortfolioIds(portfolioIds);
      } else {
        setSelectedPortfolioIds([]);
      }

      if (hasSpecificAccess(basketIds)) {
        setSelectedBasketIds(basketIds);
      } else {
        setSelectedBasketIds([]);
      }

      setIsExternal(subscription.subscriptionSettings.isSubscribedExternallyToProvider === true);
      setSubmitError(null);
    }
  }, [isOpen, subscription]);

  const togglePortfolio = (id: number) => {
    setSelectedPortfolioIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const toggleBasket = (id: number) => {
    setSelectedBasketIds((prev) =>
      prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id]
    );
  };

  const buildSubscriptionSettings = (): SubscriptionSettings => {
    let portfolios: number[] = [];
    let baskets: number[] = [];

    if (portfolioAccess === "all") {
      portfolios = [];
    } else if (portfolioAccess === "none") {
      portfolios = [-1];
    } else {
      portfolios = selectedPortfolioIds;
    }

    if (basketAccess === "all") {
      baskets = [];
    } else if (basketAccess === "none") {
      baskets = [-1];
    } else {
      baskets = selectedBasketIds;
    }

    return {
      portfolios,
      baskets,
      isSubscribedExternallyToProvider: isExternal,
    };
  };

  const isFormValid = (): boolean => {
    // If specific access is selected, at least one item must be selected
    if (portfolioAccess === "specific" && selectedPortfolioIds.length === 0) return false;
    if (basketAccess === "specific" && selectedBasketIds.length === 0) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      await onSubmit(subscription.id, buildSubscriptionSettings());
    } catch (err) {
      console.error("Submit error:", err);
      setSubmitError(err instanceof Error ? err.message : "Failed to update subscription");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AdminModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Subscription"
      closeDisabled={submitting}
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isFormValid() || submitting}
            className="flex items-center rounded-lg bg-rp-amber px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </button>
        </div>
      }
    >
            {/* User Info (read-only) */}
            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Subscriber
              </label>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-rp-separator dark:bg-rp-surface">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {subscription.subscriptionUser.email}
                </p>
                {subscription.subscriptionUser.name && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {subscription.subscriptionUser.name}
                  </p>
                )}
              </div>
            </div>

            {/* Portfolio Access */}
            <SubscriptionAccessEditor
              title="Portfolio Access"
              name="portfolioAccess"
              access={portfolioAccess}
              setAccess={setPortfolioAccess}
              items={providerPortfolios}
              selectedIds={selectedPortfolioIds}
              toggleItem={togglePortfolio}
              itemLabel="portfolios"
              contentLoading={contentLoading}
            />

            {/* Basket Access */}
            <SubscriptionAccessEditor
              title="Basket Access"
              name="basketAccess"
              access={basketAccess}
              setAccess={setBasketAccess}
              items={providerBaskets}
              selectedIds={selectedBasketIds}
              toggleItem={toggleBasket}
              itemLabel="baskets"
              contentLoading={contentLoading}
            />

            {/* Subscription Source */}
            <div className="mb-6">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={isExternal}
                  onChange={(e) => setIsExternal(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-rp-amber focus:ring-rp-amber dark:border-rp-separator"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    External subscription
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Enabled for admin-provisioned or imported subscriptions.
                  </span>
                </span>
              </label>
            </div>

            {submitError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                {submitError}
              </div>
            )}
    </AdminModalShell>
  );
};

export default EditSubscriptionModal;
